import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ModuleIcon } from "@/components/ModuleIcon";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Lock, ArrowUpRight, ExternalLink, HardHat } from "lucide-react";
import { ConstructionBadge } from "@/components/ConstructionBadge";
import { OpenModuleButton } from "@/components/OpenModuleButton";

export default function ModulesPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modules, setModules] = useState([]);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    api.get("/dashboard/modules").then(({ data }) => setModules(data)).catch(() => {});
  }, []);

  // Ouverture directe d'un module via ?open=<key> (deep-link depuis les autres apps)
  useEffect(() => {
    const key = searchParams.get("open");
    if (!key || modules.length === 0) return;
    const m = modules.find((x) => x.key === key);
    setSearchParams({}, { replace: true });
    if (m && m.has_access && !m.construction) openModule(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, searchParams]);

  const openModule = async (m) => {
    if (m.key === "support") {
      navigate("/support");
      return;
    }
    if (!m.has_access) {
      toast.error(t("dash.pages.modules.tNotInPlan"));
      return;
    }
    setOpening(m.key);
    try {
      const { data } = await api.post("/sso/token", { module: m.key });
      const url = `${data.url}?sso_token=${data.sso_token}`;
      window.open(url, "_blank", "noopener");
      toast.success(t("dash.pages.modules.tOpened").replace("{name}", m.name));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("dash.pages.modules.tCantOpen"));
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="modules-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white">{t("dash.pages.modules.title")}</h1>
        <p className="mt-1 text-white/55">
          {t("dash.pages.modules.desc")}
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {modules.map((m) => {
          const accent = m.color === "gold" ? "#EAB308" : "#dc143c";
          const locked = !m.has_access;
          return (
            <div
              key={m.key}
              data-testid={`dash-module-${m.key}`}
              className={`relative flex flex-col rounded-lg border p-6 transition-colors ${
                locked ? "border-white/8 bg-[#080808] opacity-70" : "border-white/10 bg-[#0a0a0a] hover:border-white/25"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-md border" style={{ borderColor: `${accent}44`, background: `${accent}12` }}>
                  <ModuleIcon name={m.icon} className="w-5 h-5" style={{ color: accent }} />
                </div>
                {m.premium && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase"
                    style={
                      m.premium_unlocked
                        ? { color: accent, background: `${accent}18`, border: `1px solid ${accent}33` }
                        : { color: "#888", background: "#ffffff08", border: "1px solid #ffffff15" }
                    }
                  >
                    <Crown className="w-3 h-3" /> {m.premium}
                    {!m.premium_unlocked && ` ${t("dash.pages.modules.locked")}`}
                  </span>
                )}
              </div>

              <h3 className="mt-5 text-lg font-bold text-white">{m.name}</h3>
              <p className="text-xs font-mono uppercase tracking-wide mt-0.5" style={{ color: accent }}>{t(`moduleData.${m.key}.tagline`)}</p>
              {m.key === "support" && (
                <span className="mt-2 self-start inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase text-[#22c55e] bg-[#22c55e18] border border-[#22c55e33]" data-testid="support-free-badge">
                  {lang === "en" ? "Free for all" : "Gratuit pour tous"}
                </span>
              )}
              {m.construction && <ConstructionBadge className="mt-2 self-start" />}
              <p className="mt-2 text-sm text-white/50 flex-1">{t(`moduleData.${m.key}.description`)}</p>

              {m.connected && !locked && !m.construction ? (
                <OpenModuleButton moduleKey={m.key} name={m.name} className="mt-5 w-full" />
              ) : (
                <Button
                  data-testid={`open-module-${m.key}`}
                  onClick={() => (m.construction ? null : locked ? navigate(`/tarification?module=${m.key}`) : openModule(m))}
                  disabled={m.construction || opening === m.key}
                  className={`mt-5 w-full rounded-md font-semibold gap-2 transition-colors ${
                    m.construction
                      ? "bg-white/5 text-white/40 border border-white/10 cursor-not-allowed"
                      : locked
                      ? "bg-black text-[#EAB308] border border-[#EAB308] hover:bg-[#EAB308] hover:text-black"
                      : "bg-[#EAB308] text-black hover:opacity-90"
                  }`}
                >
                  {m.construction ? (
                    <><HardHat className="w-4 h-4" /> {t("dash.pages.modules.soon")}</>
                  ) : locked ? (
                    <><Lock className="w-4 h-4" /> {t("dash.pages.modules.unlock")}</>
                  ) : opening === m.key ? (
                    t("dash.pages.modules.opening")
                  ) : (
                    <><ExternalLink className="w-4 h-4" /> {t("dash.pages.modules.open")}</>
                  )}
                </Button>
              )}
              {locked && !m.construction && (
                <p className="mt-2 text-[11px] text-center text-white/35">
                  {t("dash.pages.modules.unlockHint")}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
